import { forwardRef, useImperativeHandle, useRef } from 'react'

import { AgentKind, AgentKindProvider } from '@/components/AgentChat/AgentKindProvider'
import { cn } from '@/lib/utils'
import { AskCaiComposer } from './AskCaiComposer'
import { useAskCaiHandoff } from './useAskCaiHandoff'

export interface AskCaiChatHandle {
  focus: () => void
  submitMessage: (message: string) => void
}

interface AskCaiProps {
  /**
   * Grow the composer to fill the parent's height instead of sizing to its
   * content. For grid rows that have to line up with a taller neighbour.
   */
  fillHeight?: boolean
}

/**
 * The Ask Cai entry point a page embeds — a composer, and nothing else.
 *
 * The conversation itself lives in `AskCaiDrawer`, mounted once in `AppLayout`.
 * Submitting here picks up the most recent thread (or starts one), hands the
 * message over, and slides the panel in; from that point the user is in the
 * same panel the internal agent uses, with the same thread dropdown and tool
 * cards.
 */
export const AskCai = forwardRef<AskCaiChatHandle, AskCaiProps>(function AskCaiChat({ fillHeight }, ref) {
  const containerRef = useRef<HTMLDivElement>(null)

  return (
    // The composer lists and creates threads too, so it needs the panel's API.
    <AgentKindProvider kind={AgentKind.ASK_CAI}>
      <div ref={containerRef} className={cn(fillHeight && 'h-full')}>
        <AskCaiComposerWithHandoff ref={ref} containerRef={containerRef} fillHeight={fillHeight} />
      </div>
    </AgentKindProvider>
  )
})

interface HandoffProps extends AskCaiProps {
  containerRef: React.RefObject<HTMLDivElement | null>
}

const AskCaiComposerWithHandoff = forwardRef<AskCaiChatHandle, HandoffProps>(
  function AskCaiComposerWithHandoff({ containerRef, fillHeight }, ref) {
    const { send: handleSend, isPending, hasThreads, open, toggle } = useAskCaiHandoff()

    useImperativeHandle(ref, () => ({
      focus() {
        const textarea = containerRef.current?.querySelector('textarea')
        textarea?.focus()
        textarea?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      },
      submitMessage(message: string) {
        handleSend({ message, documentIds: [], attachments: [] })
      },
    }))

    return (
      <AskCaiComposer
        onSubmit={handleSend}
        disabled={isPending}
        handleOpenMessages={hasThreads && !open ? toggle : undefined}
        // The textarea grows into the spare space so the send button stays
        // pinned to the bottom of the taller box rather than floating mid-card.
        fillHeight={fillHeight}
      />
    )
  }
)
